import React, { ReactElement, ReactNode, useMemo, useState } from 'react'
import styles from './SplashPageContainer.module.scss'
import SwipeableViews from 'react-swipeable-views'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import { SplashPageContainerInfo } from '@/components/static/SplashPage/SplashPageTopSection/SplashPageContainer/SplashPageContainerInfo/SplashPageContainerInfo'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { LinkItem } from '@/components/static/ui/LinkItem/LinkItem'
import { CourseInfo, ReleaseStatus } from 'common/src/api/core/courses/types'
import { AuthenticatorRoutingProps } from '@/components/static/Authenticator/Authenticator.types'
import { SplashPageProps } from '@/components/sharedFetchStaticProps/fetchSplashPageProps'
import { buildFrontendLogger } from '@/logging/FrontendLogger'
import { SplashPageTopCTAButtons } from '@/components/static/SplashPageSharedComponents/SplashPageTopCTAButtons/SplashPageTopCTAButtons'
import {
    CourseOfferingGroup,
    groupCoursesByOffering,
} from '@/utils/courseOfferings'

interface SplashPageTopSectionContainerProps extends SplashPageProps {
    setIsLoginModalVisible: (isLoginModalVisible: boolean) => void
    setLoginModalRoutingPurpose: (
        loginModalRoutingPurpose: AuthenticatorRoutingProps
    ) => void
}

const logger = buildFrontendLogger('SplashPageTopSectionContainer')

export const SplashPageContainer: React.FC<
    SplashPageTopSectionContainerProps
> = (props): ReactElement => {
    const [offeringIndex, setOfferingIndex] = useState<number>(0)
    // selected exam tab per group, so each group remembers its selection
    const [buttonIndexByOffering, setButtonIndexByOffering] = useState<
        Record<string, number>
    >({})
    const offeringGroups = useMemo((): CourseOfferingGroup[] => {
        logger.info(`CourseInfo: ${JSON.stringify(props.coursesInfo)}`)
        logger.info(`props: ${JSON.stringify(props)}`)
        return groupCoursesByOffering(
            props.coursesInfo.filter(
                (courseInfo) =>
                    courseInfo.releaseInfo.releaseStatus ===
                    ReleaseStatus.fullyAvailable
            )
        )
    }, [props])
    const fullyReleasedCourseInfo = useMemo(
        (): CourseInfo[] => offeringGroups[offeringIndex]?.courses ?? [],
        [offeringGroups, offeringIndex]
    )
    const offeringButtons = useMemo((): ReactElement[] => {
        return offeringGroups.map((group, index) => (
            <div
                key={group.offering}
                className={`${styles.offeringButton} ${
                    offeringIndex === index ? styles.isActive : ''
                }`}
                onClick={() => {
                    setOfferingIndex(index)
                }}
            >
                {group.offering}
            </div>
        ))
    }, [offeringGroups, offeringIndex])
    // Every group's pane is rendered into the same grid cell, so the card is
    // always sized to the largest group and does not resize when switching
    // groups. Each pane is shifted sideways by its distance from the selected
    // group, which makes switching slide horizontally (clipped by the stack).
    const offeringPanes = useMemo((): ReactNode => {
        // with the Health / Life switch showing, keep the exam tabs even for a
        // single-exam group so every group's pane has the same layout
        const showCourseButtons = (group: CourseOfferingGroup): boolean =>
            offeringGroups.length > 1 || group.courses.length > 1
        return offeringGroups.map((group, groupIndex) => {
            const isActiveGroup = groupIndex === offeringIndex
            const groupButtonIndex = buttonIndexByOffering[group.offering] ?? 0
            const setGroupButtonIndex = (index: number): void =>
                setButtonIndexByOffering((previous) => ({
                    ...previous,
                    [group.offering]: index,
                }))
            return (
                <div
                    key={group.offering}
                    className={`${styles.offeringPane} ${
                        isActiveGroup ? '' : styles.isInactive
                    }`}
                    style={{
                        transform: `translateX(${
                            (groupIndex - offeringIndex) * 100
                        }%)`,
                    }}
                    aria-hidden={!isActiveGroup}
                >
                    {showCourseButtons(group) && (
                        <div className={styles.splashPageContainerSubTitle}>
                            Select your test
                        </div>
                    )}
                    {showCourseButtons(group) && (
                        <div
                            className={
                                styles.splashPageContainerButtonContainer
                            }
                        >
                            {group.courses.map((courseInfo, index) => (
                                <div
                                    key={courseInfo.name}
                                    className={`${
                                        styles.splashPageContainerButton
                                    } ${
                                        groupButtonIndex === index
                                            ? styles.isActive
                                            : ''
                                    }`}
                                    onClick={() => setGroupButtonIndex(index)}
                                >
                                    {`${courseInfo.abbreviation}`}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className={styles.splashPageContainerContentContainer}>
                        <SwipeableViews
                            index={groupButtonIndex}
                            onChangeIndex={setGroupButtonIndex}
                            slideClassName={reusableCssClass.sliderContainer}
                        >
                            {group.courses.map((courseInfo) => (
                                <SplashPageContainerInfo
                                    key={courseInfo.name}
                                    prettyCourseName={courseInfo.prettyName}
                                    numNotecards={
                                        courseInfo.contentStats.numNotecards
                                    }
                                    numOutlines={
                                        courseInfo.contentStats.numOutlines
                                    }
                                    numPracticeProblems={
                                        courseInfo.contentStats
                                            .numPracticeProblems
                                    }
                                    nextSittingDate={courseInfo.nextSittingDate}
                                />
                            ))}
                        </SwipeableViews>
                    </div>
                </div>
            )
        })
    }, [offeringGroups, offeringIndex, buttonIndexByOffering])

    return (
        <div>
            <div className={styles.splashPageContainer}>
                <div className={styles.text}>
                    {offeringGroups.length > 1 ? (
                        <div className={styles.offeringButtonContainer}>
                            {offeringButtons}
                        </div>
                    ) : (
                        <div className={styles.splashPageContainerTitle}>
                            {offeringGroups[0]?.offering}
                        </div>
                    )}
                    <div className={styles.offeringPaneStack}>
                        {offeringPanes}
                    </div>
                </div>
                <SplashPageTopCTAButtons
                    courseName={
                        fullyReleasedCourseInfo[
                            buttonIndexByOffering[
                                offeringGroups[offeringIndex]?.offering
                            ] ?? 0
                        ]?.name
                    }
                    setIsLoginModalVisible={props.setIsLoginModalVisible}
                    setLoginModalRoutingPurpose={
                        props.setLoginModalRoutingPurpose
                    }
                />
            </div>
            <div className={styles.splashPageSmallTextContainer}>
                <LinkItem
                    href={{
                        pathname: UiLayoutConstant.COURSES_PAGE,
                    }}
                    className={styles.splashPageSmallText}
                >
                    View all our exams and plans
                </LinkItem>
            </div>
        </div>
    )
}
